import { type Link, linkWithUrl } from '@eneco/cms-types';

import type { BreadCrumbItem } from './bread-crumbs-content.types';

/** One page of the Sanity page query's `breadcrumbTrail` (`queries.ts#buildBreadcrumbTrailProjection`). */
interface TrailEntry {
  breadcrumbTitle?: string | null;
  hidePageFromBreadcrumbs?: boolean | null;
  isHome?: boolean | null;
  link?: Link;
}

const isEntry = (value: unknown): value is TrailEntry => typeof value === 'object' && value !== null;

/** A URL segment as text; a malformed escape (a lone `%`) is left as it is rather than thrown. */
const decoded = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

/**
 * A crumb's name from its address, as Sitecore names it: the last segment of the URL, its hyphens as
 * spaces and its first letter capitalised (`/mkb/inspiratie/for-love-of-film/` → "For love of film",
 * `/en/about-us/` → "About us"), and "Home" for a site's root. A home's language segment (`/en/`) is no
 * name of its own, so an English home on a site without a path prefix is "Home" too.
 */
const nameFromUrl = (href: string, isHome: boolean): string => {
  const segments = new URL(href, 'https://base.invalid').pathname.split('/').filter(Boolean);
  const named = isHome && /^[a-z]{2}$/i.test(segments[0] ?? '') ? segments.slice(1) : segments;
  const segment = named.pop();
  if (!segment) return 'Home';
  const words = decoded(segment).replaceAll('-', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** The trail up to and including the first site home: a home's own ancestors, another site's, are no crumbs. */
const toFirstHome = (entries: TrailEntry[]): TrailEntry[] => {
  const home = entries.findIndex(entry => entry.isHome);
  return home === -1 ? entries : entries.slice(0, home + 1);
};

/**
 * The breadcrumbs Sitecore works out on the server (`BreadCrumbs`' `breadCrumbs`), from the Sanity page's
 * trail of itself and its ancestors, as far as its site home: root first, each named by its
 * `breadcrumbTitle` or else its address, and without the pages marked `hidePageFromBreadcrumbs`, the page
 * itself included.
 */
export const crumbsFromTrail = (trail: unknown): BreadCrumbItem[] =>
  Array.isArray(trail)
    ? toFirstHome(trail.filter(isEntry))
        .reverse()
        .flatMap(entry => {
          const link = linkWithUrl(entry.link);
          if (entry.hidePageFromBreadcrumbs || !link) return [];
          const name = entry.breadcrumbTitle?.trim() || nameFromUrl(link.href, Boolean(entry.isHome));
          return [{ name, url: link.href, linktype: 'internal' }];
        })
    : [];
